/**
 * PanelManagerBee - Phase A Mission A2
 *
 * Manages the state of Studio v2's three panel slots.
 * This is a simple in-memory state container for v1.
 * Later we can persist to localStorage or backend.
 *
 * Key responsibilities:
 * - Maintain typed state for center-main, right-rail, and drawer slots
 * - Validate panel IDs against StudioPanelRegistry
 * - Notify subscribers when state changes
 * - Emit telemetry events for debugging/correlation
 */

import {
    type StudioPanelSlot,
    isPanelIdValid,
    canPanelOccupySlot,
    getDefaultPanelForSlot,
} from './registry.js';

// ============================================
// STATE TYPES
// ============================================

/**
 * The current state of Studio v2's panel layout
 */
export interface StudioPanelState {
    /** Panel ID for the center-main slot (e.g., "missions-main") */
    centerMainId: string;
    /** Panel ID for the right-rail slot (e.g., "soma-noesis") */
    rightRailId: string;
    /** Panel ID for the drawer slot (e.g., "drawer-terminal"), undefined if closed */
    drawerId?: string;
}

// ============================================
// IN-MEMORY STATE CONTAINER
// ============================================

/**
 * Default state - matches current Studio v2 behavior
 */
const defaultState: StudioPanelState = {
    centerMainId: 'missions-main',
    rightRailId: 'soma-noesis',
    drawerId: 'drawer-terminal',
};

/**
 * Current panel state (in-memory for v1)
 */
let studioPanelState: StudioPanelState = { ...defaultState };

// ============================================
// SUBSCRIPTION SYSTEM (simple event emitter)
// ============================================

type StateSubscriber = (state: StudioPanelState) => void;
const subscribers: Set<StateSubscriber> = new Set();

/**
 * Notify all subscribers of state change
 */
function notifySubscribers(state: StudioPanelState): void {
    subscribers.forEach(callback => {
        try {
            callback(state);
        } catch (error) {
            console.error('[PanelManager] Subscriber error:', error);
        }
    });
}

/**
 * Subscribe to panel state changes
 * @returns Unsubscribe function
 */
export function subscribeToStudioPanelState(callback: StateSubscriber): () => void {
    subscribers.add(callback);
    return () => {
        subscribers.delete(callback);
    };
}

// ============================================
// STATE ACCESSORS
// ============================================

/**
 * Get the current studio panel state
 */
export function getStudioPanelState(): StudioPanelState {
    return { ...studioPanelState };
}

/**
 * Update the studio panel state with validation
 *
 * Invalid panel IDs are ignored with a warning (no throwing in v1).
 * This ensures the UI never crashes from a bad command.
 */
export function setStudioPanelState(patch: Partial<StudioPanelState>): void {
    const newState = { ...studioPanelState };
    let hasChanges = false;

    // Validate and apply centerMainId
    if (patch.centerMainId !== undefined && patch.centerMainId !== studioPanelState.centerMainId) {
        if (
            isPanelIdValid(patch.centerMainId) &&
            canPanelOccupySlot(patch.centerMainId, 'center-main')
        ) {
            newState.centerMainId = patch.centerMainId;
            hasChanges = true;
        } else {
            console.warn(
                `[PanelManager] Invalid centerMainId: "${patch.centerMainId}" - must be a valid center-main panel`
            );
        }
    }

    // Validate and apply rightRailId
    if (patch.rightRailId !== undefined && patch.rightRailId !== studioPanelState.rightRailId) {
        if (
            isPanelIdValid(patch.rightRailId) &&
            canPanelOccupySlot(patch.rightRailId, 'right-rail')
        ) {
            newState.rightRailId = patch.rightRailId;
            hasChanges = true;
        } else {
            console.warn(
                `[PanelManager] Invalid rightRailId: "${patch.rightRailId}" - must be a valid right-rail panel`
            );
        }
    }

    // Validate and apply drawerId (can be undefined to close drawer)
    if (patch.drawerId !== undefined && patch.drawerId !== studioPanelState.drawerId) {
        if (isPanelIdValid(patch.drawerId) && canPanelOccupySlot(patch.drawerId, 'drawer')) {
            newState.drawerId = patch.drawerId;
            hasChanges = true;
        } else {
            console.warn(
                `[PanelManager] Invalid drawerId: "${patch.drawerId}" - must be a valid drawer panel`
            );
        }
    } else if (patch.drawerId === undefined && studioPanelState.drawerId !== undefined) {
        // Explicitly closing the drawer
        newState.drawerId = undefined;
        hasChanges = true;
    }

    // Only update and notify if something changed
    if (hasChanges) {
        studioPanelState = newState;

        // A2.4: Telemetry - log state changes for debugging
        console.log('[PanelManager] State changed:', studioPanelState);

        // Notify subscribers
        notifySubscribers(studioPanelState);
    }
}

/**
 * Reset panel state to defaults
 */
export function resetStudioPanelState(): void {
    studioPanelState = { ...defaultState };
    console.log('[PanelManager] State reset to defaults:', studioPanelState);
    notifySubscribers(studioPanelState);
}

// ============================================
// CONVENIENCE HELPERS
// ============================================

/**
 * Set the center-main panel by ID
 */
export function setCenterMainPanel(panelId: string): void {
    setStudioPanelState({ centerMainId: panelId });
}

/**
 * Set the center-main panel by app key (e.g., "builder", "convops")
 */
export function setCenterMainByAppKey(appKey: string): void {
    // Map app keys to panel IDs
    const panelId = `${appKey}-main`;
    setStudioPanelState({ centerMainId: panelId });
}

/**
 * Set the drawer panel by ID
 */
export function setDrawerPanel(panelId: string): void {
    setStudioPanelState({ drawerId: panelId });
}

/**
 * Toggle the drawer open/closed
 */
export function toggleDrawer(panelId?: string): void {
    if (studioPanelState.drawerId) {
        // Drawer is open - close it or switch tab
        if (panelId && panelId !== studioPanelState.drawerId) {
            // Switch to different drawer tab
            setStudioPanelState({ drawerId: panelId });
        } else {
            // Close drawer (set to undefined)
            setStudioPanelState({ drawerId: undefined });
        }
    } else {
        // Drawer is closed - open it
        const targetPanel = panelId ?? getDefaultPanelForSlot('drawer')?.id ?? 'drawer-terminal';
        setStudioPanelState({ drawerId: targetPanel });
    }
}
